import { themeVar } from '@/styles/theme';
import { formatCash } from '@/shared/utils/currency';
import styled from 'styled-components';

const getBackgroundColor = ({
  isActive,
  isNextItemActive,
  isPrevItemActive,
  isLastItem
}: {
  isActive: boolean;
  isNextItemActive: boolean;
  isPrevItemActive: boolean;
  isLastItem: boolean;
}) => {
  if (isNextItemActive) {
    return isPrevItemActive ? themeVar('action.active') : themeVar('layer.12');
  }

  if (isActive) {
    if (isLastItem && isPrevItemActive) {
      return themeVar('action.active');
    }
    return isPrevItemActive ? themeVar('action.active') : themeVar('layer.12');
  }

  if (isPrevItemActive) {
    return themeVar('action.active');
  }

  return themeVar('layer.12');
};

export interface IVisualFilteringBarData {
  id: string;
  name: string;
  order: number;
  count: number;
  totalCommittedCapital: number;
  enabled: boolean;
}

interface VisualFilteringBarProps {
  filterBar: IVisualFilteringBarData[];
  onChangeFilterBar: (filterBarItems: IVisualFilteringBarData[]) => void;
}

const VisualFilteringBar = ({ filterBar, onChangeFilterBar }: VisualFilteringBarProps) => {
  const handleSelect = (id: number) => {
    const newData = [...filterBar];
    newData[id].enabled = !newData[id].enabled;
    onChangeFilterBar(newData);
  };

  return (
    <VisualFilteringBarWrapper>
      {filterBar.map((item, index) => (
        <FilterBarItemWrapper
          key={index}
          isActive={item.enabled}
          isNextItemActive={filterBar[index + 1]?.enabled}
          isPrevItemActive={filterBar[index - 1]?.enabled}
          isFirstItem={index === 0}
          isLastItem={index === filterBar.length - 1}
          onClick={() => handleSelect(index)}
        >
          <FilterBarItem>
            <FilterBarItemTitle>{item.name}</FilterBarItemTitle>
            <FilterBarItemDetails>
              <FilterBarItemCount>{item.count}</FilterBarItemCount>
              <FilterBarItemAmount>{formatCash(item.totalCommittedCapital)}</FilterBarItemAmount>
            </FilterBarItemDetails>
          </FilterBarItem>
        </FilterBarItemWrapper>
      ))}
    </VisualFilteringBarWrapper>
  );
};

export default VisualFilteringBar;

const VisualFilteringBarWrapper = styled.div`
  display: flex;
  padding: 40px 0;
  width: 100%;
  cursor: pointer;
`;

const FilterBarItemWrapper = styled.div<{
  isActive: boolean;
  isNextItemActive: boolean;
  isPrevItemActive: boolean;
  isFirstItem: boolean;
  isLastItem: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: ${({ isFirstItem, isLastItem }) => (isFirstItem ? '8px 0 0 8px' : isLastItem ? '0 8px 8px 0' : '0')};
  width: 100%;
  background: ${({ isActive }) => (isActive ? themeVar('action.active') : themeVar('layer.12'))};
  transition: 0.3s ease-in-out;
  color: ${({ isActive }) => (isActive ? themeVar('layer.base') : themeVar('font.weak'))};

  &::before {
    content: '';
    width: 51px;
    height: 51px;
    border-top: 1px solid ${themeVar('border.base')};
    border-right: 1px solid ${themeVar('border.base')};
    background-color: ${({ isActive, isNextItemActive, isPrevItemActive, isLastItem }) =>
      getBackgroundColor({ isActive, isNextItemActive, isPrevItemActive, isLastItem })};
    transform: rotate(45deg);
    display: ${({ isFirstItem }) => (isFirstItem ? 'none' : 'block')};
    position: relative;
    left: -26px;
    transition: 0.3s ease-in-out;
  }
`;

const FilterBarItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 5px 20px 20px;
  position: relative;
  z-index: 1;
`;

const FilterBarItemTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  line-height: 10px;
`;

const FilterBarItemDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterBarItemCount = styled.div`
  font-size: 36px;
  font-weight: 700;
  line-height: 20px;
`;

const FilterBarItemAmount = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 14px;
`;
